/**
 * deathCell — pure px→tile mapping for the Tier 2 death-heatmap (Feature F).
 *
 * World fires a death hook from the real-death sim paths (enemy / pit / timeout /
 * hazard); this converts the player's body to the tile cell the death is attributed
 * to, clamped to the grid so an off-world fall (player far below the floor) still maps
 * to a valid in-bounds cell. Pure + integer-only (Math.floor/min/max) → ESLint-clean
 * under src/engine (no transcendentals / wall-clock) and unit-testable headless.
 */

/** The cause of a real death (client-side annotation; the server aggregates x/y only). */
export type DeathCause = 'enemy' | 'pit' | 'timeout' | 'hazard';

/** One reported death cell. `cause` is forward-compat metadata (server keys on x,y). */
export interface DeathCell {
  x: number;
  y: number;
  cause: DeathCause;
}

/**
 * Map a player body (x/w/y/h in world px) to the tile cell its death belongs to.
 * Uses the body's horizontal centre and vertical centre, divides by tileSize, and
 * CLAMPS to [0, cols-1] × [0, rows-1] — so a pit death (body.y far below the grid)
 * attributes to the bottom row at the column the player fell from, not an off-grid cell.
 */
export function deathCell(
  bodyX: number, bodyW: number, bodyY: number, bodyH: number,
  tileSize: number, cols: number, rows: number, cause: DeathCause,
): DeathCell {
  const cx = bodyX + bodyW / 2;
  const cy = bodyY + bodyH / 2;
  const x = Math.max(0, Math.min(cols - 1, Math.floor(cx / tileSize)));
  const y = Math.max(0, Math.min(rows - 1, Math.floor(cy / tileSize)));
  return { x, y, cause };
}
